import { getCookie } from 'cookies-next'
import { useEffect, useRef, useState } from 'react'

import type { CodeErrorReason } from '@/server/routers/flows/utils/codeValidation'
import { reportError } from '@/services/errorReporting'
import { clearCookie, saveCookie } from '@/utils/cookies'
import { cookieNames } from '@helpers/cookies/utils'

export type AppliedCoupon = { code: string; amount: number }

interface ValidateCodeMutationResult {
  success: boolean
  code: string
  amount: number
  type?: 'coupon_code' | 'referral_code'
  referrerName?: string
  errorReason?: CodeErrorReason
  transient?: boolean
}

interface UseCouponOptions {
  /**
   * The flow session's values from the latest step query. Only a coupon held
   * here counts as applied, since the server prices and purchases with it.
   */
  sessionFlowValues?: Record<string, unknown> | undefined
  /** The `?coupon=` parameter of the current URL, if any. */
  urlCode?: string | undefined
  hasFlowSession?: boolean
  /** Maps a rejected code to the localized message shown under the input. */
  getErrorMessage: (reason: CodeErrorReason) => string
}

const REVALIDATED_CODE_KEY = '_gs-coupon-revalidated'

const readRevalidatedCode = (): string | null => {
  try {
    return sessionStorage.getItem(REVALIDATED_CODE_KEY)
  } catch (error: unknown) {
    reportError(error, { context: '[coupon] sessionStorage read failed' })
    return null
  }
}

const markRevalidatedCode = (code: string) => {
  try {
    sessionStorage.setItem(REVALIDATED_CODE_KEY, code)
  } catch (error: unknown) {
    reportError(error, { context: '[coupon] sessionStorage write failed' })
  }
}

const readCookieCode = () => {
  const cookieCode = getCookie(cookieNames.coupon)
  return typeof cookieCode === 'string' ? cookieCode.trim() : ''
}

const findCodeBeforeSession = (urlCode: string | undefined): string | null => {
  if (typeof window === 'undefined') {
    return null
  }
  return urlCode?.trim() || readCookieCode() || null
}

/**
 * Coupon UI state shared by all checkout flows. The flow session is the source
 * of truth: a coupon is applied once the session holds it. A code that only
 * the cookie or the `?coupon=` parameter carries surfaces as `codeToValidate`;
 * the caller validates it, which writes it to the session, and hands the
 * result to `applyAutoResult`. A code the session still lacks is queued again
 * with each new session snapshot. Without a flow session yet, the code is
 * queued on mount so the caller can validate it before the session starts.
 *
 * Callers wire the tRPC `validateCode` mutation themselves and pass the
 * result through `applyResult` — that keeps the hook product-agnostic and
 * dodges the tRPC type plumbing. User service decides whether the code is a
 * coupon or a referral; only a coupon becomes applied state here, a referral
 * is stored as the referral cookie and surfaces via the referral discount.
 */
export const useCoupon = (options: UseCouponOptions) => {
  const [code, setCode] = useState('')
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [codeToValidate, setCodeToValidate] = useState<string | null>(() =>
    options.hasFlowSession === false ? findCodeBeforeSession(options.urlCode) : null
  )
  const [rejectedCode, setRejectedCode] = useState<string | null>(null)
  const syncedSnapshot = useRef<Record<string, unknown> | undefined>(undefined)

  useEffect(() => {
    const isUsableCode = (value: unknown): value is string =>
      typeof value === 'string' && value !== '' && value !== rejectedCode
    const asApplied = (value: unknown, amount: unknown): AppliedCoupon | null =>
      isUsableCode(value) && typeof amount === 'number' && Number.isFinite(amount) && amount >= 0
        ? { code: value, amount }
        : null

    const cookieCode = readCookieCode()
    const sessionValues = options.sessionFlowValues

    const urlCode = options.urlCode?.trim()
    const sessionMissesCookieCode =
      sessionValues !== undefined &&
      sessionValues !== syncedSnapshot.current &&
      isUsableCode(cookieCode) &&
      sessionValues.coupon_code !== cookieCode
    if (urlCode && urlCode !== rejectedCode && readRevalidatedCode() !== urlCode) {
      setCodeToValidate(urlCode)
    } else if (sessionMissesCookieCode) {
      syncedSnapshot.current = sessionValues
      setCodeToValidate(cookieCode)
    }

    if (appliedCoupon || !sessionValues) {
      return
    }
    const sessionCoupon = asApplied(sessionValues.coupon_code, sessionValues.coupon_amount)
    if (sessionCoupon) {
      setAppliedCoupon(sessionCoupon)
    }
  }, [appliedCoupon, options.sessionFlowValues, options.urlCode, rejectedCode])

  const acceptCoupon = (coupon: AppliedCoupon) => {
    setAppliedCoupon(coupon)
    saveCookie(cookieNames.coupon, coupon.code)
    saveCookie(cookieNames.couponAmount, String(coupon.amount))
  }

  const applyResult = (result: ValidateCodeMutationResult) => {
    if (!result.success) {
      setError(options.getErrorMessage(result.errorReason ?? 'invalid'))
      return
    }

    setError(null)
    setRejectedCode(null)
    syncedSnapshot.current = options.sessionFlowValues

    if (result.type === 'referral_code') {
      setAppliedCoupon(null)
      return
    }

    acceptCoupon({ code: result.code, amount: result.amount })
  }

  const dropCoupon = (droppedCode: string | null) => {
    setRejectedCode(droppedCode)
    setAppliedCoupon(null)
    clearCookie(cookieNames.coupon)
    clearCookie(cookieNames.couponAmount)
  }

  const applyAutoResult = (result: ValidateCodeMutationResult | null) => {
    syncedSnapshot.current = options.sessionFlowValues
    setCodeToValidate(null)

    if (!result || (!result.success && result.transient !== false)) {
      return
    }

    markRevalidatedCode(result.code)

    if (!result.success || result.type === 'referral_code') {
      dropCoupon(result.code)
      return
    }

    setRejectedCode(null)
    acceptCoupon({ code: result.code, amount: result.amount })
  }

  const clear = () => {
    dropCoupon(appliedCoupon?.code ?? null)
    setCodeToValidate(null)
    setCode('')
    setError(null)
  }

  return {
    code,
    setCode,
    clear,
    appliedCoupon,
    codeToValidate,
    error,
    applyResult,
    applyAutoResult,
  }
}
